// Business Card Controller
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["favoriteButton"]
  static values = { siteId: Number }

  connect() {
    // Add intersection observer for lazy loading
    this.observeIntersection()
    
    // Add analytics tracking
    this.setupAnalytics()
  }

  disconnect() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
  }

  toggleFavorite(event) {
    event.preventDefault()
    
    const button = this.favoriteButtonTarget
    const heart = button.querySelector('svg')
    const wasOutline = heart.classList.contains('text-gray-400')
    
    // Optimistic UI update
    if (wasOutline) {
      heart.classList.remove('text-gray-400')
      heart.classList.add('text-rose-500')
      // Change from outline to solid (would need to swap SVG or use data attributes)
    } else {
      heart.classList.remove('text-rose-500')
      heart.classList.add('text-gray-400')
      // Change from solid to outline
    }
    
    // Submit the form (this will be handled by Turbo)
    button.click()
    
    // Track analytics
    this.trackFavoriteAction(wasOutline ? 'add' : 'remove')
  }

  // Handle card view tracking
  cardViewed() {
    if (!this.hasViewedCard) {
      this.hasViewedCard = true
      this.trackAnalytics('business_card_viewed', {
        site_id: this.siteIdValue,
        timestamp: new Date().toISOString()
      })
    }
  }

  // Handle click tracking
  cardClicked(event) {
    this.trackAnalytics('business_card_clicked', {
      site_id: this.siteIdValue,
      element: event.target.tagName.toLowerCase(),
      timestamp: new Date().toISOString()
    })
  }

  // Handle book now button clicks
  bookNowClicked(event) {
    this.trackAnalytics('book_now_clicked', {
      site_id: this.siteIdValue,
      source: 'business_card',
      timestamp: new Date().toISOString()
    })
  }

  // Private methods

  observeIntersection() {
    if (!window.IntersectionObserver) return
    
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.cardViewed()
        }
      })
    }, {
      threshold: 0.5,
      rootMargin: '50px'
    })
    
    this.intersectionObserver.observe(this.element)
  }

  setupAnalytics() {
    // Add click listeners for analytics
    this.element.addEventListener('click', this.cardClicked.bind(this))
    
    // Track book now button specifically
    const bookButton = this.element.querySelector('[href*="appointment"]')
    if (bookButton) {
      bookButton.addEventListener('click', this.bookNowClicked.bind(this))
    }
  }

  trackFavoriteAction(action) {
    this.trackAnalytics('favorite_action', {
      action: action,
      site_id: this.siteIdValue,
      timestamp: new Date().toISOString()
    })
  }

  trackAnalytics(event, data) {
    // Send to analytics service (Google Analytics, Mixpanel, etc.)
    if (window.gtag) {
      window.gtag('event', event, data)
    }
    
    // Also send to internal analytics if available
    if (window.KozenetAnalytics) {
      window.KozenetAnalytics.track(event, data)
    }
    
    // Console log for development
    if (process.env.NODE_ENV === 'development') {
      console.log('Analytics:', event, data)
    }
  }

  // Animation helpers
  animateIn() {
    this.element.style.opacity = '0'
    this.element.style.transform = 'translateY(20px)'
    
    requestAnimationFrame(() => {
      this.element.style.transition = 'all 0.3s ease-out'
      this.element.style.opacity = '1'
      this.element.style.transform = 'translateY(0)'
    })
  }

  animateOut() {
    return new Promise(resolve => {
      this.element.style.transition = 'all 0.3s ease-in'
      this.element.style.opacity = '0'
      this.element.style.transform = 'translateY(-20px)'
      
      setTimeout(resolve, 300)
    })
  }
}
